import { useCallback, useEffect, useState } from "react";
import { CalendarDays, Phone, Users, Check, CheckCheck, Ban, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUS = {
  nouvelle: { label: "Nouvelle", cls: "bg-[#C5A059]/15 text-[#8C6D23]" },
  confirmee: { label: "Confirmée", cls: "bg-[#2D5A27]/10 text-[#2D5A27]" },
  honoree: { label: "Honorée", cls: "bg-[#1C1917]/5 text-[#57534E]" },
  annulee: { label: "Annulée", cls: "bg-[#6B1226]/10 text-[#6B1226]" },
};

const dayLabel = (iso) => {
  const d = new Date(iso + "T12:00:00");
  const s = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export default function AdminReservations() {
  const today = new Date().toLocaleDateString("sv-SE");
  const [date, setDate] = useState(today);
  const [upcoming, setUpcoming] = useState(false);
  const [items, setItems] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [keyyo, setKeyyo] = useState(null);

  useEffect(() => {
    api.get("/admin/keyyo/log").then((res) => setKeyyo(res.data)).catch(() => setKeyyo(null));
  }, []);

  const load = useCallback(() => {
    const url = upcoming ? "/admin/reservations" : `/admin/reservations?date=${date}`;
    api.get(url).then((res) => setItems(res.data)).catch(() => setItems([]));
  }, [date, upcoming]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (r, status) => {
    try {
      await api.patch(`/admin/reservations/${r.id}`, { status });
      toast.success(`Réservation ${STATUS[status].label.toLowerCase()} — ${r.customer_name}, ${r.time.replace(":", "h")}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (r) => {
    if (confirmDelete !== r.id) {
      setConfirmDelete(r.id);
      return;
    }
    try {
      await api.delete(`/admin/reservations/${r.id}`);
      toast.success("Réservation supprimée");
      setConfirmDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="mt-8" data-testid="admin-reservations-panel">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <CalendarDays size={17} className="text-[#6B1226]" />
        <input
          type="date"
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
            setUpcoming(false);
          }}
          className="bg-white border border-[#6B1226]/15 rounded-xl px-3.5 py-2 text-sm outline-none focus:border-[#6B1226]/50 transition-colors"
          data-testid="admin-reservations-date"
        />
        <button
          onClick={() => setDate(today)}
          className="rounded-full border border-[#6B1226]/25 text-[#6B1226] px-4 py-2 text-xs font-semibold hover:bg-[#6B1226]/5 transition-colors"
          data-testid="admin-reservations-today"
        >
          Aujourd'hui
        </button>
        <button
          onClick={() => setUpcoming((v) => !v)}
          className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
            upcoming ? "bg-[#6B1226] text-[#FAF6F0]" : "border border-[#6B1226]/25 text-[#6B1226] hover:bg-[#6B1226]/5"
          }`}
          data-testid="admin-reservations-upcoming"
        >
          Toutes à venir
        </button>
      </div>

      {keyyo && (
        <div className="mb-5 bg-white rounded-2xl border border-[#6B1226]/10 px-4 py-3.5 flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="admin-keyyo-status">
          <p className="text-xs font-bold text-[#1C1917] flex items-center gap-2">
            <Phone size={13} className="text-[#6B1226]" /> SMS automatique sur appel manqué
          </p>
          <span
            className={`rounded-full px-3 py-1 text-[0.65rem] font-semibold ${keyyo.configured ? "bg-[#2D5A27]/10 text-[#2D5A27]" : "bg-[#8C6D23]/10 text-[#8C6D23]"}`}
            data-testid="admin-keyyo-configured"
          >
            {keyyo.configured ? "Activé" : "Activation en cours"}
          </span>
          <span className="text-xs text-[#57534E]" data-testid="admin-keyyo-count">
            {keyyo.missed_calls} appel{keyyo.missed_calls > 1 ? "s" : ""} manqué{keyyo.missed_calls > 1 ? "s" : ""} détecté{keyyo.missed_calls > 1 ? "s" : ""} · {keyyo.sms.filter((s) => s.ok).length} SMS envoyé{keyyo.sms.filter((s) => s.ok).length > 1 ? "s" : ""}
          </span>
          <span className="text-[0.65rem] italic text-[#A8A29E]">« {keyyo.message} »</span>
        </div>
      )}

      {items === null ? (
        <p className="text-sm text-[#57534E] italic">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#6B1226]/10 p-8 text-center" data-testid="admin-reservations-empty">
          <p className="font-display text-xl text-[#1C1917]">Aucune réservation</p>
          <p className="text-sm text-[#57534E] mt-1.5">{upcoming ? "Aucune table réservée pour les jours à venir." : "Aucune table réservée ce jour-là."}</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-reservations-list">
          {items.map((r, i) => {
            const st = STATUS[r.status] || STATUS.nouvelle;
            return (
              <li
                key={r.id}
                className={`bg-white rounded-2xl border border-[#6B1226]/10 p-4 sm:p-5 ${r.status === "annulee" || r.status === "honoree" ? "opacity-60" : ""}`}
                data-testid={`admin-reservation-${i}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="text-center shrink-0">
                      <p className="font-display text-2xl font-semibold text-[#6B1226] leading-none" data-testid={`admin-reservation-time-${i}`}>
                        {r.time.replace(":", "h")}
                      </p>
                      <p className="text-[0.6rem] font-eyebrow tracking-[0.14em] uppercase text-[#A8A29E] mt-1">{r.service}</p>
                    </div>
                    <span className="self-stretch w-px bg-[#6B1226]/10 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1C1917] truncate" data-testid={`admin-reservation-name-${i}`}>
                        {r.customer_name}
                        <span className="ml-2 font-normal text-[#57534E]">
                          · {r.guests} couvert{r.guests > 1 ? "s" : ""}
                        </span>
                      </p>
                      <p className="text-xs text-[#57534E] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span>{dayLabel(r.date)}</span>
                        {r.customer_phone && (
                          <a href={`tel:${r.customer_phone}`} className="inline-flex items-center gap-1 text-[#6B1226] font-semibold hover:underline" data-testid={`admin-reservation-phone-${i}`}>
                            <Phone size={11} /> {r.customer_phone}
                          </a>
                        )}
                      </p>
                      {r.note && <p className="text-xs italic text-[#8C6D23] mt-1">« {r.note} »</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    <span className={`rounded-full px-3 py-1 text-[0.65rem] font-semibold ${st.cls}`} data-testid={`admin-reservation-status-${i}`}>
                      {st.label}
                    </span>
                    {r.status === "nouvelle" && (
                      <button
                        onClick={() => setStatus(r, "confirmee")}
                        className="inline-flex items-center gap-1 rounded-full bg-[#2D5A27] text-[#FAF6F0] px-3.5 py-1.5 text-[0.65rem] font-semibold hover:bg-[#24491F] transition-colors"
                        data-testid={`admin-reservation-confirm-${i}`}
                      >
                        <Check size={12} /> Confirmer
                      </button>
                    )}
                    {(r.status === "nouvelle" || r.status === "confirmee") && (
                      <>
                        <button
                          onClick={() => setStatus(r, "honoree")}
                          className="inline-flex items-center gap-1 rounded-full border border-[#1C1917]/20 text-[#57534E] px-3.5 py-1.5 text-[0.65rem] font-semibold hover:bg-[#1C1917]/5 transition-colors"
                          data-testid={`admin-reservation-honor-${i}`}
                        >
                          <CheckCheck size={12} /> Honorée
                        </button>
                        <button
                          onClick={() => setStatus(r, "annulee")}
                          className="inline-flex items-center gap-1 rounded-full border border-[#6B1226]/25 text-[#6B1226] px-3.5 py-1.5 text-[0.65rem] font-semibold hover:bg-[#6B1226]/5 transition-colors"
                          data-testid={`admin-reservation-cancel-${i}`}
                        >
                          <Ban size={12} /> Annuler
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => remove(r)}
                      className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[0.65rem] font-semibold transition-colors ${
                        confirmDelete === r.id ? "bg-[#6B1226] text-[#FAF6F0]" : "text-[#A8A29E] hover:text-[#6B1226]"
                      }`}
                      data-testid={`admin-reservation-delete-${i}`}
                      aria-label="Supprimer cette réservation"
                    >
                      <Trash2 size={12} /> {confirmDelete === r.id ? "Confirmer ?" : ""}
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
